import { useState } from 'react';
import ScrollSpy from 'react-ui-scrollspy';
import { css } from 'styled-components';

import color from 'atoms/colors/colors';
import { fontSize, lineHeight } from 'atoms/typography/typography';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Grid from 'quarks/Grid';

import Section from 'molecules/Section/Section';

import SidebarAuthor from 'components/BlogPost/SidebarAuthor';
import CategoryLinks from 'components/ResourceComponents/CategoryLinks';
import LegalDisclaimer from 'components/ResourceComponents/LegalDisclaimer';
import NewsletterSignup from 'components/ResourceComponents/NewsletterSignup';
import TableOfContents from 'components/TableOfContents/TableOfContents';

import richTextParser from 'utils/richTextParser';

import type { ContentfulComponentRichText, ContentfulTemplateBlogPost } from 'graphqlTypes';
import type { FC } from 'react';

interface BlogDetailProps extends ContentfulTemplateBlogPost {
  readTime: string;
}

const richBodyCSS = css`
  > div {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 16px;
    font-size: ${fontSize.textLg};
  }
  h1 {
    font-size: ${fontSize.displayXs};
    line-height: ${lineHeight.displayXs};
    color: ${color.gray[900]};
  }
  h2:not(.embed-card h2) {
    font-size: ${fontSize.textXl};
    line-height: ${lineHeight.textXl};
    color: ${color.gray[700]};
  }
  h3:not(.embed-card h3) {
    font-size: ${fontSize.textLg};
    line-height: ${lineHeight.textLg};
    color: ${color.gray[700]};
  }
  h4:not(.embed-card h4),
  h5:not(.embed-card h5),
  h6:not(.embed-card h6) {
    font-size: ${fontSize.textLg};
    line-height: ${lineHeight.textLg};
  }
  blockquote {
    padding-top: 32;
    padding-bottom: 32;
    margin: 0;
    font-size: ${fontSize.textSm};
    line-height: ${lineHeight.textSm};
    color: ${color.gray[500]};
  }
`;

const BlogDetail: FC<BlogDetailProps> = ({ readTime, body, author, publishDate, tableOfContents }) => {
  const [activeId, setActiveId] = useState('');

  return (
    <Section id="blog-post-body">
      <Grid
        gridTemplateColumns="1fr"
        xl={{
          gridTemplateColumns: tableOfContents && tableOfContents.length > 0 ? '270px 570px 270px' : '840px 270px',
          gap: '30px',
        }}
      >
        {tableOfContents && tableOfContents.length > 0 && (
          <Flex width="100%" marginBottom={32}>
            <Flex
              position="sticky"
              top="calc(var(--header-height, 100px) + 16px)"
              width="100%"
              border="1px solid"
              borderColor="gray-300"
              borderRadius="4px"
              height="fit-content"
              paddingY={16}
              paddingX={24}
              xl={{ paddingAll: 32 }}
            >
              <TableOfContents
                activeId={activeId}
                label="Table of Contents"
                tableOfContents={tableOfContents as ContentfulComponentRichText['tableOfContents']}
              />
            </Flex>
          </Flex>
        )}
        <Flex
          flexDirection="column"
          width="100%"
          gap="24px"
          marginBottom={28}
          paddingBottom={64}
          borderBottom="1px solid"
          borderBottomColor="gray-300"
          md={{ gap: '32px' }}
          xl={{ border: 'none', paddingBottom: 0, marginBottom: 0 }}
        >
          {body && (
            <Flex id="resource-body-content" css={richBodyCSS}>
              <ScrollSpy
                scrollThrottle={25}
                onUpdateCallback={(id: string) => {
                  setActiveId(id);
                }}
              >
                {body && richTextParser(body)}
              </ScrollSpy>
            </Flex>
          )}
          <LegalDisclaimer />
        </Flex>
        <Container position="relative">
          <CategoryLinks marginBottom={24} md={{ marginBottom: 32 }} />
          <Flex
            flexDirection="column"
            width="100%"
            gap="24px"
            md={{ gap: '32px' }}
            xl={{ position: 'sticky', top: 'calc(var(--header-height, 100px) + 16px)' }}
          >
            <NewsletterSignup />
            {author && <SidebarAuthor {...author} readTime={readTime} publishDate={publishDate} />}
          </Flex>
        </Container>
      </Grid>
    </Section>
  );
};

export default BlogDetail;
